import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import Icon from "../components/Icon";
import { useWallet } from "../hooks/useWallet";
import { getUserPositions, getMarketById } from "../services/marketService";
import { getUserPredictions, getUserStats } from "../services/predictionService";

const UserDashboard = () => {
  const { address, isConnected, balance, balanceSymbol } = useWallet();
  const [positions, setPositions] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Format wallet address
  const formatAddress = (addr) => {
    if (!addr) return "Not Connected";
    return `${addr.slice(0, 6)}...${addr.slice(-6)}`;
  };

  // Fetch user data from Firebase
  useEffect(() => {
    const fetchUserData = async () => {
      if (!isConnected || !address) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch positions and predictions in parallel
        const [trades, priceBets, userStats] = await Promise.all([
          getUserPositions(address),
          getUserPredictions(address),
          getUserStats(address),
        ]);

        // Transform trades to positions format
        const positionsData = await Promise.all(
          trades.map(async (trade) => {
            const market = await getMarketById(trade.marketId);
            return {
              id: trade.id,
              market: market?.title || "Unknown Market",
              outcome: trade.side,
              totalStaked: trade.amount,
              recentTrades: trade.timestamp?.toDate 
                ? trade.timestamp.toDate().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                : "Unknown",
              trades: 1, // Could aggregate if needed
              price: trade.price,
              value: trade.amount,
            };
          })
        );

        // Transform predictions
        const predictionsData = priceBets.map((pred) => ({
          id: pred.id,
          symbol: pred.symbol,
          direction: pred.direction,
          amount: pred.amount,
          currentPrice: pred.currentPrice,
          targetPrice: pred.targetPrice,
          status: pred.status,
          expiry: pred.expiry,
          profitLoss: pred.profitLoss,
        }));

        setPositions(positionsData);
        setPredictions(predictionsData);
        setStats(userStats);
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [isConnected, address]);

  // Mock positions data for fallback
  const mockPositions = [
    {
      id: 1,
      market: "BNB all time high by December 31?",
      outcome: "YES",
      totalStaked: 0.0,
      recentTrades: "Dec 1, 02:22 PM",
      trades: 1,
      price: 0.52,
      value: 0.0,
    },
    {
      id: 2,
      market: "Unknown Market",
      outcome: "YES",
      totalStaked: 0.12,
      recentTrades: "Dec 1, 01:15 PM",
      trades: 3,
      price: 0.45,
      value: 0.12,
    },
  ];

  const totalStaked = positions.reduce((sum, pos) => sum + (pos.totalStaked || 0), 0);
  const totalPredictions = predictions.reduce((sum, pred) => sum + (pred.amount || 0), 0);

  return (
    <Layout activePage="user-dashboard">
      <div className="min-h-screen pt-6 pb-20 px-4 lg:px-6">
        <div className="max-w-7xl mx-auto">
          {/* My Dashboard Section */}
          <div className="mb-8 animate-fade-in-up">
            <h1 className="text-4xl md:text-5xl font-black text-white mb-6" style={{ fontFamily: 'monospace' }}>
              My Dashboard
            </h1>
            
            <div className="border border-yellow-500 rounded-lg p-6 bg-[#0a0a0a]">
              <div className="mb-4">
                <p className="text-gray-400 text-sm mb-1">Wallet Address</p>
                <p className="text-orange-500 font-mono text-sm">
                  {isConnected ? formatAddress(address) : "0x0000...0000"}
                </p>
              </div>
              <div className="mb-6">
                <p className="text-gray-400 text-sm mb-1">Total Staked</p>
                <p className="text-white font-black text-4xl" style={{ fontFamily: 'monospace' }}>
                  ${(totalStaked + totalPredictions).toFixed(2)}
                </p>
              </div>
              {stats && (
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-gray-400 text-xs mb-1">Active Predictions</p>
                    <p className="text-white font-bold text-lg">{stats.active || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-1">Win Rate</p>
                    <p className="text-white font-bold text-lg">{stats.winRate ? `${stats.winRate.toFixed(1)}%` : '0%'}</p>
                  </div>
                </div>
              )}
              
              <div className="flex gap-4">
                <Link
                  to="/markets"
                  className="flex-1 bg-yellow-500 text-black font-black py-3 px-6 rounded-lg text-center hover:bg-yellow-400 transition-colors"
                >
                  Browse Markets
                </Link>
                <Link
                  to="/create-market"
                  className="flex-1 bg-white text-black font-black py-3 px-6 rounded-lg text-center hover:bg-gray-200 transition-colors"
                >
                  Create Market
                </Link>
              </div>
            </div>
          </div>

          {/* Your Positions Section */}
          <div className="animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6" style={{ fontFamily: 'monospace' }}>
              Your Positions
            </h2>
            
            {loading ? (
              <div className="text-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-4"></div>
                <p className="text-gray-400">Loading positions...</p>
              </div>
            ) : positions.length === 0 ? (
              <div className="text-center py-10 border border-white/10 rounded-lg bg-[#0a0a0a]">
                <Icon name="Wallet" size={48} className="mx-auto mb-4 text-gray-600" />
                <p className="text-gray-400">No positions found</p>
                <Link
                  to="/markets"
                  className="mt-4 inline-block bg-yellow-500 text-black font-black py-2 px-6 rounded-lg hover:bg-yellow-400 transition-colors"
                >
                  Browse Markets
                </Link>
              </div>
            ) : (
            <div className="space-y-4">
              {positions.map((position) => (
                <div
                  key={position.id}
                  className="border border-yellow-500 rounded-lg p-6 bg-[#0a0a0a]"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-white font-bold text-lg mb-2">{position.market}</h3>
                    </div>
                    <button className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded">
                      {position.outcome}
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400 mb-1">Total Staked</p>
                      <p className="text-white font-bold">${position.totalStaked.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 mb-1">Recent Trades:</p>
                      <p className="text-white font-bold">{position.recentTrades}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 mb-1">Trades</p>
                      <p className="text-white font-bold">{position.trades}</p>
                    </div>
                    <div className="text-right md:text-left">
                      <p className="text-gray-400 mb-1">Price</p>
                      <p className="text-white font-bold">${position.value.toFixed(2)} @ ${position.price.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
                </div>
              )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UserDashboard;
